import {
    PublicationType,
    RequiredAccess,
    requiredAccessValue,
} from '@news-mono/web-common'
import React from 'react'
import {
    StyledCommentsIcon,
    StyledEditorialLabel,
    StyledHeadline,
    StyledHeadlineEditorialLabel,
    StyledHeadlineText,
    StyledKickerEditorialLabel,
    StyledPremiumIcon,
} from '../../cards/CardText/CardText.styled'
import { IconTypes, Kicker, KickerMode } from '../../cards/Kicker/Kicker'
import { EditorialType } from '../../templates/Publication/lib/get-editorial-type'
import { appendToHeadline } from '../../__helpers/append-to-headline'
import { useProduct } from '../../__product/useProduct'
import { FontScales } from '../../__styling/settings/fontScale'

export interface CardHeadline {
    hasBackground?: boolean
    kickerMode?: KickerMode
    className?: string
    isLarge?: boolean
    fontScale: FontScales
    headlineText: string
    kickerText?: string
    publicationKind: PublicationType['kind']
    isSponsored?: boolean
    insideSponsoredCollection?: boolean
    editorialType?: EditorialType
    showVideoIcon?: boolean
    requiredAccess?: RequiredAccess
    showCommentsIcon?: boolean
    isLiveSportsTopic?: boolean
}
// eslint-disable-next-line no-redeclare
export const CardHeadline: React.FC<CardHeadline> = function CardHeadline(
    props,
) {
    const iconWidth = 16
    const iconPremiumHeight = 16
    const product = useProduct()
    const {
        fontScale,
        hasBackground,
        kickerText,
        kickerMode,
        headlineText,
        className,
        isLarge,
        publicationKind,
        editorialType,
        showVideoIcon,
        showCommentsIcon,
        requiredAccess,
        insideSponsoredCollection,
        isLiveSportsTopic,
    } = props

    const perthNowTheme = product === 'perthnow'

    const showKicker = perthNowTheme
        ? kickerMode !== 'hidden' &&
          !props.isSponsored &&
          publicationKind !== 'gallery'
        : (kickerMode !== 'hidden' && !props.isSponsored) ||
          (kickerMode !== 'hidden' &&
              product === 'thewest' &&
              insideSponsoredCollection === true) // show kicker in sponsored collection

    const showPremiumIcon =
        requiredAccess?.level === 'subscriber' ||
        requiredAccess?.level === 'registered'

    // Set Icon Types for Kicker
    const iconTypes: Array<IconTypes> = []
    showVideoIcon && iconTypes.push('video')
    showPremiumIcon && iconTypes.push('premium')
    publicationKind === 'gallery' && iconTypes.push('gallery')
    showCommentsIcon && iconTypes.push('comments')

    return (
        <StyledHeadline fontScale={fontScale} className={className}>
            {/* Editorial Label shown above the kicker mostly on PerthNow */}
            {editorialType && (
                <StyledEditorialLabel
                    type={editorialType}
                    cardHasBackground={hasBackground}
                    hasKicker={showKicker && kickerMode !== 'hidden'}
                />
            )}
            {showKicker &&
                kickerMode !== 'hidden' &&
                kickerMode?.kickerVisibility !== 'hidden' && (
                    <Kicker
                        size={kickerMode?.size || isLarge ? 'large' : 'default'}
                        kickerPosition={kickerMode?.kickerPosition}
                        icons={kickerMode?.icons ?? iconTypes}
                        kickerVisibility={kickerMode?.kickerVisibility}
                        kickerType={kickerMode?.kickerType}
                        requiredAccess={requiredAccess}
                    >
                        {/* Editorial Label shown on The West inline with kickers */}
                        {editorialType && (
                            <StyledKickerEditorialLabel type={editorialType} />
                        )}
                        {kickerText}
                    </Kicker>
                )}
            <StyledHeadlineText
                fontScale={fontScale}
                className={className}
                cardHasBackground={hasBackground}
                isLarge={isLarge}
            >
                {/* Editorial Label shown on The West next to the title only on some places like the latest news page */}
                {editorialType && (
                    <StyledHeadlineEditorialLabel type={editorialType} />
                )}
                {showCommentsIcon &&
                    (kickerMode === 'hidden' ||
                        kickerMode?.kickerVisibility) && (
                        <StyledCommentsIcon kickerMode={kickerMode} />
                    )}
                {showPremiumIcon &&
                    (kickerMode === 'hidden' || kickerMode?.kickerVisibility) &&
                    requiredAccessValue(requiredAccess, {
                        subscriber: (
                            <StyledPremiumIcon
                                width={iconWidth}
                                height={iconPremiumHeight}
                                kickerMode={kickerMode}
                            />
                        ),
                        anonymous: undefined,
                    })}
                {isLiveSportsTopic
                    ? appendToHeadline(headlineText, editorialType)
                    : headlineText}
            </StyledHeadlineText>
        </StyledHeadline>
    )
}
CardHeadline.displayName = 'CardHeadline'
